import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RegisterRequest } from '../../models/register-request.interface';
import { LoginRequest } from '../../models/login-request.interface';
import { AuthResponse } from '../../models/auth-response.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/labase`;
  private readonly authApiUrl = `${environment.apiUrl}/auth`;

  constructor(private http: HttpClient) {}

  /**
   * Connexion utilisateur (étudiants uniquement)
   * La validation du profil "étudiant" est maintenant gérée côté serveur pour plus de sécurité.
   * @param credentials - Email, mot de passe et remember me
   * @returns Promise contenant l'utilisateur et le token JWT (+ refresh token si remember me)
   */
  login(credentials: LoginRequest): Promise<AuthResponse> {
    return firstValueFrom(
      this.http.post<AuthResponse>(
        `${this.apiUrl}/auth/login`,
        {
          email: credentials.email,
          password: credentials.password,
          rememberMe: credentials.rememberMe
        }
      )
    );
  }

  /**
   * Renouvelle l'access token avec un refresh token
   * @param refreshToken - Le refresh token
   * @returns Promise contenant le nouvel access token
   */
  refreshToken(refreshToken: string): Promise<{ access_token: string }> {
    return firstValueFrom(
      this.http.post<{ access_token: string }>(
        `${this.authApiUrl}/refresh`,
        { refreshToken }
      )
    );
  }

  /**
   * Vérifier si un email existe déjà
   * @param email - Email à vérifier
   * @returns Promise contenant { exists: boolean }
   */
  checkEmailExists(email: string): Promise<{ exists: boolean }> {
    return firstValueFrom(
      this.http.get<{ exists: boolean }>(`${this.apiUrl}/check-email/${encodeURIComponent(email)}`)
    );
  }

  /**
   * Vérifier si un numéro de téléphone existe déjà
   * @param tel - Numéro de téléphone à vérifier
   * @returns Promise contenant { exists: boolean }
   */
  checkPhoneExists(tel: string): Promise<{ exists: boolean }> {
    return firstValueFrom(
      this.http.get<{ exists: boolean }>(`${this.apiUrl}/check-phone/${encodeURIComponent(tel)}`)
    );
  }

  /**
   * Inscription utilisateur
   * @param userData - Données d'inscription
   * @returns Promise contenant l'utilisateur créé et le token JWT
   */
  register(userData: RegisterRequest): Promise<AuthResponse> {
    return firstValueFrom(
      this.http.post<AuthResponse>(
        `${this.apiUrl}/auth/register`,
        userData
      )
    );
  }

  /**
   * Récupération du profil utilisateur
   * @returns Promise contenant les données du profil
   */
  getProfile(): Promise<any> {
    return firstValueFrom(
      this.http.get(`${this.apiUrl}/auth/profile`)
    );
  }

  refreshTokenJwt(): Promise<{ access_token: string }> {
    return firstValueFrom(
      this.http.post<{ access_token: string }>(`${this.apiUrl}/auth/refresh-token`, {})
    );
  }

  /**
   * Mise à jour du profil utilisateur
   * @param userId - ID de l'utilisateur
   * @param profileData - Données à mettre à jour (nom, prenom, tel)
   * @returns Promise contenant le message de succès et le nouveau access_token (avec données utilisateur dedans)
   */
  updateProfile(userId: number, profileData: { nom?: string; prenom?: string; tel?: string }): Promise<{ message: string; access_token: string }> {
    return firstValueFrom(
      this.http.put<{ message: string; access_token: string }>(`${this.apiUrl}/users/${userId}`, profileData)
    );
  }

  /**
   * Suppression du compte utilisateur
   * Cette action est irréversible et supprime :
   * - Le certificat FTP
   * - Le contact Brevo
   * - Les données LaBase de l'utilisateur
   * - Le lien avec l'application LaBase
   * - Si aucune autre app n'est liée, le compte central est également supprimé
   *
   * @returns Promise contenant le résultat de la suppression
   */
  deleteAccount(): Promise<{ message: string; deletedFromCentralAuth: boolean }> {
    return firstValueFrom(
      this.http.delete<{ message: string; deletedFromCentralAuth: boolean }>(
        `${this.apiUrl}/account`
      )
    );
  }

  /**
   * Changer le magasin d'un utilisateur
   * Met à jour le champ app_data.labase.magasinId
   *
   * @param userId - UUID de l'utilisateur
   * @param magasinId - ID du nouveau magasin
   * @returns Promise contenant le message de succès et le nouveau access_token (avec données utilisateur dedans)
   */
  changeMagasin(userId: string, magasinId: number): Promise<{ message: string; access_token: string }> {
    return firstValueFrom(
      this.http.put<{ message: string; access_token: string }>(
        `${this.authApiUrl}/users/${userId}/magasin`,
        { magasinId }
      )
    );
  }

  /**
   * Demande de réinitialisation de mot de passe
   * Envoie un email avec un lien de réinitialisation si l'email existe
   *
   * @param email - Email de l'utilisateur
   * @returns Promise contenant un message de confirmation
   */
  forgotPassword(email: string): Promise<{ message: string }> {
    return firstValueFrom(
      this.http.post<{ message: string }>(
        `${this.apiUrl}/auth/forgot-password`,
        { email }
      )
    );
  }

  /**
   * Réinitialise le mot de passe avec un token valide
   *
   * @param token - Token reçu par email
   * @param newPassword - Nouveau mot de passe
   * @returns Promise contenant un message de confirmation
   */
  resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return firstValueFrom(
      this.http.post<{ message: string }>(
        `${this.apiUrl}/auth/reset-password`,
        { token, newPassword }
      )
    );
  }

  /**
   * Envoie un code OTP à l'email étudiant
   *
   * @param email - Email universitaire de l'étudiant
   * @returns Promise void (code envoyé par email)
   */
  sendOTPCode(email: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${this.apiUrl}/student-verification/send`,
        { email }
      )
    );
  }

  /**
   * Vérifie le code OTP saisi par l'utilisateur
   *
   * @param email - Email universitaire de l'étudiant
   * @param code - Code OTP à 6 chiffres
   * @returns Promise contenant { valid: boolean }
   */
  verifyOTPCode(email: string, code: string): Promise<{ valid: boolean }> {
    return firstValueFrom(
      this.http.post<{ valid: boolean }>(
        `${this.apiUrl}/student-verification/verify`,
        { email, code }
      )
    );
  }

  /**
   * Met à jour l'email étudiant de l'utilisateur
   *
   * @param data - Email et code OTP de vérification
   * @returns Promise contenant le message de succès et le nouveau access_token
   */
  updateStudentEmail(data: { email: string; code: string }): Promise<{ message: string; access_token: string }> {
    return firstValueFrom(
      this.http.put<{ message: string; access_token: string }>(
        `${this.apiUrl}/auth/student-email`,
        data
      )
    );
  }
}
